import { HTTP_HEADERS } from '../../auth/const';
import type { QueryMeta } from '../fetch/types';
import { addTenantPrefix } from '../tenantBasePath';
import { getPublicApiURLString } from './getPublicApiURLString';
import { getPublicPageURLString } from './getPublicPageURLString';

const AUTH_TOKEN_KEY = 'uaAuthToken';

/** splits on the first occurrence of `sep`, preserving any later occurrences in the second element */
const splitAt = (str: string, sep: string): [string, string] => {
  const idx = str.indexOf(sep);
  return idx === -1 ? [str, ''] : [str.slice(0, idx), str.slice(idx + 1)];
};

/**
 * appends query params to a url string without requiring a URL object.
 * handles hash fragments by extracting them, adding query params, then reattaching the hash.
 */
const appendSearchParams = (url: string, params: Record<string, string>): string => {
  if (!Object.keys(params).length) return url;

  const [base, hash] = splitAt(url, '#');
  const [path, query] = splitAt(base, '?');
  const searchParams = new URLSearchParams(query);

  for (const [key, value] of Object.entries(params)) {
    searchParams.set(key, value);
  }

  const qs = searchParams.toString();

  return `${path}${qs ? `?${qs}` : ''}${hash ? `#${hash}` : ''}`;
};

/**
 * string-based file URL adaptation for server-side (no domain / no URL object).
 * applies public API/page transforms, attaches auth token and preview params,
 * then prepends the tenant basePath.
 */
export const adaptFileUrlWithoutDomain = (
  url: string,
  queryMeta: QueryMeta | undefined,
  options: { preview?: boolean } = { preview: true },
): string => {
  const uaAuthToken =
    queryMeta?.headers?.[HTTP_HEADERS.AuthToken] ||
    queryMeta?.headers?.[HTTP_HEADERS.ClientAuthToken];

  let adaptedUrl = url;

  if (queryMeta?.isPublicInterface) {
    adaptedUrl = getPublicApiURLString(adaptedUrl);
  }

  // Mirror the ky network hook gating — only anonymous visitors on a public
  // page get the /auth/* rewrite. Logged-in users keep /api/* so their session
  // is honored on direct browser fetches (image/video/citation src).
  if (queryMeta?.isPublicPage && !queryMeta?.isUserLoggedIn) {
    adaptedUrl = getPublicPageURLString(adaptedUrl);
  }

  const params: Record<string, string> = {};
  if (uaAuthToken) {
    params[AUTH_TOKEN_KEY] = uaAuthToken;
  }
  if (options?.preview) {
    params.preview = 'true';
  }
  adaptedUrl = appendSearchParams(adaptedUrl, params);

  return addTenantPrefix(adaptedUrl, queryMeta?.basePath);
};
